import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { needsPhantomBrowser, apriInPhantom } from '../lib/phantomMobile';
import usePhantomConnect from '../lib/usePhantomConnect';
import { segretoDaIndirizzo, consegnaToken, urlDiRitorno, tornaAlBrowser } from '../lib/consegnaSessione';
import HowToPlay from './HowToPlay';
import SettingsMenu from './SettingsMenu';
import { login as authLogin, devLogin, authHeader, getToken, clearToken } from '../lib/authClient';
import { requestEntryTicket } from '../lib/entryClient';
import { deposita, fetchChainConfig } from '../lib/depositClient';
import { preleva, fetchWithdrawConfig } from '../lib/withdrawClient';
import { fetchPricing } from '../lib/entryClient';
import { getSolPrice, conStimaInDollari } from '../lib/solPrice';

import './LandingPage.css';

// Pseudo-wallet della scorciatoia di sviluppo (nessuna chiave da firmare).
// Stessa versione usata da MainScene per le texture: le anteprime della Vault
// stanno negli stessi /assets senza hash nel nome, quindi se la si alza li'
// va alzata anche qui, se no il vault continua a mostrare la skin vecchia.
const VERSIONE_ASSET = 2;

const DEV_WALLET = 'dev-wallet';

// Taglio unico del deposito, per ora. Quando servira' scegliere l'importo
// diventera' un campo: il server accredita comunque quello che legge in catena,
// quindi cambiare questo numero non tocca niente lato sicurezza.
const DEPOSITO_SOL = 0.1;

// Solo estetica: il server manda l'etichetta del tier, non l'emoji.
const TIER_EMOJI = { 1: '🟢', 2: '🟡', 3: '🔴' };

const LandingPage = ({ onEnterGame }) => {
  const { publicKey, connected: adapterConnected, signMessage, signTransaction, sendTransaction, disconnect } = useWallet();
  const { connection } = useConnection();

  // Da telefono l'estensione non esiste e l'adapter non collega niente: il
  // wallet arriva dal giro attraverso l'app Phantom (lib/phantomDeeplink).
  // Da qui in giu' le due provenienze sono indistinguibili.
  // Da telefono l'estensione non esiste: il wallet arriva dal deeplink di
  // collegamento (lib/usePhantomConnect). Da qui in giu' le due provenienze
  // sono indistinguibili — cambia solo che quella da telefono non puo' firmare,
  // e l'effetto del profilo se ne accorge da solo perche' signMessage manca.
  const phantom = usePhantomConnect();
  const walletAddress = publicKey ? publicKey.toString() : phantom.wallet;
  const connected = adapterConnected || Boolean(phantom.wallet);

  // Scollegare significa tre cose diverse a seconda di come ci si e' collegati,
  // e vanno fatte tutte: chi resta indietro tiene il giocatore agganciato al
  // wallet precedente. Il token di sessione per primo — senza, il wallet nuovo
  // erediterebbe l'autorizzazione del vecchio sulle azioni che toccano il vault.
  const handleDisconnect = useCallback(async () => {
    if (walletAddress) clearToken(walletAddress);
    setAuthed(false);
    phantom.disconnetti();
    try {
      await disconnect();
    } catch (e) {
      // L'adapter protesta se non era lui a essere collegato: non e' un guasto,
      // e lo scollegamento e' comunque avvenuto.
    }
  }, [walletAddress, disconnect, phantom]);

  // true quando il wallet ha firmato il login: senza, il vault non permette azioni.
  // Dal percorso desktop lo decide l'effetto qui sotto; da telefono lo decide il
  // ritorno dalla firma, che puo' concludersi DOPO che l'effetto ha gia' visto
  // un token assente. Le due fonti vanno unite, altrimenti chi firma da telefono
  // resta bloccato fuori dall'arena pur avendo firmato.
  // Da telefono la firma si conclude DOPO che l'effetto qui sotto ha gia' visto
  // un token assente: le due fonti vanno unite, altrimenti chi firma resta
  // bloccato fuori dall'arena pur avendo firmato.
  const [authedLocale, setAuthed] = useState(false);
  const authed = authedLocale || phantom.authed;

  // Vero quando questa pagina e' quella aperta DENTRO Phantom per firmare al
  // posto del browser normale (lib/consegnaSessione). Cambia cosa mostrare: qui
  // non si gioca, si firma e si torna indietro.
  const [consegnato, setConsegnato] = useState(false);
  const segretoConsegna = segretoDaIndirizzo();
  // L'indirizzo che riporta il giocatore nel browser da cui è partito. Vale
  // null se non sappiamo da dove veniva o se quel browser non ha un modo per
  // essere richiamato: in quel caso resta l'istruzione scritta.
  const ritorno = useMemo(() => (segretoConsegna ? urlDiRitorno() : null), [segretoConsegna]);

  // Consegna riuscita: si prova a riportarlo di là da soli. Con un attimo di
  // ritardo, così «SESSION READY» fa in tempo a leggersi e il giocatore capisce
  // perché l'app sta cambiando sotto le sue mani.
  //
  // Il salto lo tenta `tornaAlBrowser`, che prova più strade e smette appena la
  // pagina sparisce dalla vista. Qui c'era un `window.location.href` secco: è
  // la forma di navigazione che le webview scartano più facilmente, ed è il
  // motivo per cui il giocatore restava dentro Phantom dopo aver firmato.
  useEffect(() => {
    if (!consegnato || !ritorno) return undefined;

    let interrompi = () => {};
    const avvio = setTimeout(() => { interrompi = tornaAlBrowser(ritorno); }, 900);

    return () => {
      clearTimeout(avvio);
      interrompi();
    };
  }, [consegnato, ritorno]);

  // Telefono in un browser normale: il pulsante standard porterebbe al download
  // di Phantom anche a chi ce l'ha gia' installata (vedi lib/phantomMobile).
  // Non si decide una volta sola: dentro il browser di Phantom il provider a
  // volte compare un istante dopo il primo render, e senza il ricontrollo
  // proporremmo di aprire Phantom a chi e' gia' dentro Phantom.
  const [phantomBrowserNeeded, setPhantomBrowserNeeded] = useState(() => needsPhantomBrowser());
  useEffect(() => {
    if (!phantomBrowserNeeded) return undefined;
    const t = setInterval(() => setPhantomBrowserNeeded(needsPhantomBrowser()), 250);
    const stop = setTimeout(() => clearInterval(t), 2000);
    return () => { clearInterval(t); clearTimeout(stop); };
  }, [phantomBrowserNeeded]);
  
  const [selectedTier, setSelectedTier] = useState(1);
  // Vero mentre il server sta emettendo il ticket d'ingresso: non e' piu' un
  // finto caricamento a setTimeout, e' l'attesa di una risposta reale.
  const [isDeploying, setIsDeploying] = useState(false);
  const [globalBurn, setGlobalBurn] = useState(1204550);

  // Configurazione della catena: dove sta la tesoreria e se i depositi sono
  // attivi. La decide il server, non il bundle del frontend — cambiare
  // l'indirizzo della tesoreria non deve richiedere di ricompilare il client.
  const [chainConfig, setChainConfig] = useState(null);
  // Prezzi dei tier e cambio SOL: entrambi decisi fuori dal client. I prezzi li
  // dice il server (possono cambiare da .env senza ricompilare il frontend), il
  // cambio serve solo per la stima in dollari accanto alla cifra in SOL.
  const [pricingInfo, setPricingInfo] = useState(null);
  const [solUsd, setSolUsd] = useState(null);
  const [isDepositing, setIsDepositing] = useState(false);
  const [depositMsg, setDepositMsg] = useState('');

  // Prelievo. I limiti (minimo, tetto giornaliero, cooldown) li decide il
  // server: qui si mostrano soltanto, per non far scoprire all'utente un
  // rifiuto dopo che ha gia' firmato.
  const [withdrawConfig, setWithdrawConfig] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withdrawMsg, setWithdrawMsg] = useState('');
  
  // Il requisito d'accesso in $VEXL lo dichiara il server (/api/pricing) e lo
  // VERIFICA il server (/api/entry), leggendo il saldo del token in catena.
  // Qui c'era una costante `walletVexlValue = 100` confrontata dentro il
  // browser: un numero finto, uguale per tutti, in un controllo che stava dal
  // lato di chi doveva superarlo. Ora questa parte è solo informativa.

  // Dashboard Tabs
  const [activeTab, setActiveTab] = useState('ARENA'); // 'ARENA', 'VAULT', 'MISSIONS', 'HOWTO'

  // Skin Vault State (Real balance will be fetched in Phase 3)
  const [localVexlBalance, setLocalVexlBalance] = useState(0);

  // Retrieve unlocked skins from localStorage, default to #00FF00 and OGskeleton
  const [unlockedSkins, setUnlockedSkins] = useState(() => {
    const saved = localStorage.getItem('unlockedSkins');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (!parsed.includes('OGskeleton')) parsed.push('OGskeleton'); // Always give OG skeleton
        return parsed;
      } catch (e) {
        return ['#00FF00', 'OGskeleton'];
      }
    }
    return ['#00FF00', 'OGskeleton'];
  });

  const [equippedSkin, setEquippedSkin] = useState(() => {
    return localStorage.getItem('equippedSkin') || 'OGskeleton';
  });

  const availableSkins = [
    { id: 1, name: 'DEFAULT TOXIC', hex: '#39FF14', type: 'color', price: 0 },
    { id: 2, name: 'NEON VIPER', hex: '#FF00FF', type: 'color', price: 5000 },
    { id: 3, name: 'CYBER GHOST', hex: '#00FFFF', type: 'color', price: 10000 },
    { id: 4, name: 'SOLAR FLARE', hex: '#FFA500', type: 'color', price: 25000 },
    { id: 5, name: 'OG SKELETON', hex: 'OGskeleton', type: 'image', price: 0 },
    { id: 6, name: 'BINARY SNAKE', hex: 'BinarySnake', type: 'image', price: 0, lockedMessage: 'SURVIVE 60 SECONDS' },
    { id: 7, name: 'singularity', hex: 'singularity', type: 'image', price: 0 },
    { id: 8, name: 'Cybersnake', hex: 'Cybersnake', type: 'image', price: 0 },
    { id: 12, name: 'MAGMA CORE', hex: 'Magmacore', type: 'image', price: 0 },
    { id: 14, name: 'HEXCORE', hex: 'Hexcore', type: 'image', price: 0 },
    { id: 15, name: 'NULLSECTOR', hex: 'Nullsector', type: 'image', price: 0 },
    { id: 16, name: 'VEXEL PRIME', hex: 'Vexelprime', type: 'image', price: 0 },
    { id: 17, name: 'BLACKHOLE', hex: 'Blackhole', type: 'image', price: 0 },
    { id: 18, name: 'DATASTREAM', hex: 'Datastream', type: 'image', price: 0 },
    // Ricompense di livello: sbloccate salendo di livello, non acquistabili.
    { id: 10, name: 'GOLDEN BULLET', hex: 'Goldenbullet', type: 'image', price: 0, lockedMessage: 'REACH LEVEL 10' },
    { id: 11, name: 'CYBERCAR', hex: 'Cybercar', type: 'image', price: 0, lockedMessage: 'REACH LEVEL 25' }
  ];

  // Le skin appartengono al wallet e vivono sul server. localStorage resta solo
  // come cache locale: se il server non risponde si continua a vedere qualcosa,
  // ma la verita' e' quella del profilo.
  const [profileLoaded, setProfileLoaded] = useState(false);

  // Livello e progresso XP del wallet, calcolati dal server (mostrati in alto a sx).
  const [levelInfo, setLevelInfo] = useState(null);

  useEffect(() => {
    if (!walletAddress) {
      setProfileLoaded(false);
      setAuthed(false);
      setLevelInfo(null);
      return;
    }

    let cancelled = false;
    const loadProfile = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const res = await fetch(`${API_BASE}/api/profile/${walletAddress}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const profile = await res.json();
        if (cancelled) return;

        setUnlockedSkins(profile.unlockedSkins || []);
        setEquippedSkin(profile.equippedSkin);
        setLocalVexlBalance(profile.vexlBalance || 0);
        // Il saldo in SOL veniva dimenticato proprio qui: si aggiornava solo
        // dopo un acquisto o un deposito fatti nella sessione corrente, quindi
        // chi ricaricava la pagina vedeva 0 pur avendo depositato. Il saldo era
        // giusto sul server e nel ledger — spariva solo dagli occhi di chi lo
        // possiede, che è il modo peggiore di sbagliare un numero.
        setSolBalance(profile.solBalance || 0);
        setLevelInfo(profile.level || null);
        setProfileLoaded(true);

        if (profile.persistent === false) {
          console.warn('[VEXEL] Il server sta usando lo store in memoria: le skin non sopravvivono al riavvio.');
        }

        // Login a firma: chiediamo a Phantom di firmare la sfida. È qui che il
        // wallet dimostra di essere suo; senza, buy/equip verranno rifiutati.
        try {
          // Un token c'e' gia' quando si ricarica la pagina a sessione aperta:
          // richiedere un'altra firma sarebbe solo una finestra di Phantom in
          // piu' da approvare, per un permesso che abbiamo gia'.
          if (getToken(walletAddress)) {
            if (!cancelled) setAuthed(true);
          } else if (typeof signMessage === 'function') {
            await authLogin(walletAddress, signMessage);
            if (!cancelled) setAuthed(true);
          }

          // Siamo dentro Phantom per conto del browser normale: il token
          // appena ottenuto va consegnato, altrimenti resta prigioniero di
          // questo browser e chi aspetta di la' non riceve niente.
          const segreto = segretoDaIndirizzo();
          const token = getToken(walletAddress);
          if (segreto && token) {
            await consegnaToken(segreto, token);
            if (!cancelled) setConsegnato(true);
          }
          // Senza signMessage non c'e' wallet capace di firmare: il vault resta
          // in sola lettura, ma si puo' comunque entrare in partita.

        } catch (authErr) {
          // Firma rifiutata o wallet senza signMessage: il vault resta in sola
          // lettura, ma il giocatore può comunque entrare in partita.
          console.warn('[VEXEL] Login a firma non completato:', authErr.message);
          if (!cancelled) setAuthed(false);
        }
      } catch (err) {
        // Server irraggiungibile: si resta sulla cache locale, ma senza far
        // credere al giocatore che i suoi acquisti siano stati salvati.
        console.error('[VEXEL] Profilo non caricato, uso la cache locale:', err.message);
        if (!cancelled) { setProfileLoaded(false); setAuthed(false); }
      }
    };

    loadProfile();
    return () => { cancelled = true; };
  }, [walletAddress, signMessage]);

  // Cache locale, per wallet: prima era una sola chiave condivisa, quindi due
  // wallet diversi sullo stesso browser si scambiavano le skin.
  useEffect(() => {
    const key = walletAddress ? `unlockedSkins:${walletAddress}` : 'unlockedSkins';
    localStorage.setItem(key, JSON.stringify(unlockedSkins));
  }, [unlockedSkins, walletAddress]);

  useEffect(() => {
    const key = walletAddress ? `equippedSkin:${walletAddress}` : 'equippedSkin';
    localStorage.setItem(key, equippedSkin);
  }, [equippedSkin, walletAddress]);

  // Le missioni (e lo sblocco skin che ne deriva) le calcola il server sui dati
  // reali della partita: il vecchio listener 'unlock_binary_snake' era falsificabile
  // ed e' stato rimosso. Qui leggiamo solo il progresso dal server.
  const [missions, setMissions] = useState([]);

  useEffect(() => {
    if (!walletAddress) { setMissions([]); return; }
    let cancelled = false;
    const loadMissions = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const res = await fetch(`${API_BASE}/api/missions/${walletAddress}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setMissions(data.missions || []);
      } catch (err) {
        console.error('[VEXEL] Missioni non caricate:', err.message);
      }
    };
    loadMissions();
    return () => { cancelled = true; };
  }, [walletAddress]);

  // Wallet Balance State (Will be populated by Web3 provider in Phase 3)
  const [solBalance, setSolBalance] = useState(0);

  // Server fetch for global burn
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${API_BASE}/api/stats`);
        const data = await response.json();
        setGlobalBurn(data.totalBurnedVexl || 0);
      } catch (err) {
        console.error("Failed to fetch global burn stats", err);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 3000);
    return () => clearInterval(interval);
  }, []);


  const handleDeploy = async () => {
    if (!connected || !walletAddress || isDeploying) return;

    // Niente controllo d'accesso qui: lo fa /api/entry sul wallet autenticato,
    // leggendo i $VEXL dalla catena. Se non bastano risponde 403 VEXL_REQUIRED,
    // e il messaggio che arriva dice quanti ne servono e quanti ne hai.

    // Il tier non si dichiara piu' entrando in partita: si chiede al server un
    // ticket d'ingresso, ed e' quello a stabilire in che arena si finisce.
    // Senza login a firma il server non sa chi sta chiedendo di entrare.
    if (!authed) {
      alert('Sign the wallet login to enter a match.');
      return;
    }

    setIsDeploying(true);
    try {
      const ticket = await requestEntryTicket(walletAddress, selectedTier);
      onEnterGame(walletAddress, ticket.tier, equippedSkin, ticket.ticketId);
    } catch (err) {
      // 402: il saldo non basta. Non puo' succedere finche' l'ingresso e'
      // gratuito, ma il messaggio serve gia' ora — quel ramo si accende da solo
      // quando cambia il profilo di prezzo, senza altro codice da scrivere.
      alert(
        err.code === 'INSUFFICIENT_FUNDS'
          ? `Not enough balance for this tier. ${err.message}`
          // 403: non hai abbastanza $VEXL. Il messaggio del server dice già
          // quanti ne servono e quanti ne hai, quindi si mostra così com'è.
          : err.code === 'VEXL_REQUIRED'
            ? `Access denied: ${err.message}`
            : `Entry refused: ${err.message}`
      );
    } finally {
      setIsDeploying(false);
    }
  };

  // Prezzi dei tier e cambio, una volta all'avvio.
  useEffect(() => {
    let annullato = false;
    fetchPricing()
      .then((p) => { if (!annullato) setPricingInfo(p); })
      .catch(() => { if (!annullato) setPricingInfo(null); });
    getSolPrice()
      .then((v) => { if (!annullato) setSolUsd(v); })
      .catch(() => {});
    return () => { annullato = true; };
  }, []);

  // Chi ha la tesoreria e su che rete: si chiede una volta all'avvio.
  useEffect(() => {
    let annullato = false;
    fetchChainConfig()
      .then((c) => { if (!annullato) setChainConfig(c); })
      .catch(() => { if (!annullato) setChainConfig(null); });
    return () => { annullato = true; };
  }, []);

  // Deposito: il wallet firma un trasferimento verso la tesoreria, poi il
  // server rilegge la transazione dalla catena e accredita quello che ci trova.
  // Il client non dichiara nessun importo — quello era il buco di prima.
  const handleDeposit = async () => {
    if (isDepositing) return;
    if (!authed) {
      alert('Sign the wallet login to deposit.');
      return;
    }

    setIsDepositing(true);
    setDepositMsg('');
    try {
      const esito = await deposita({
        connection,
        publicKey,
        signTransaction,
        sendTransaction,
        sol: DEPOSITO_SOL,
        onProgress: setDepositMsg,
      });

      setSolBalance(esito.newBalance);
      setDepositMsg(
        esito.giaAccreditato
          ? 'This deposit had already been credited.'
          : `${esito.credited} SOL credited.`
      );
    } catch (err) {
      // "Inviata ma non ancora finalizzata" non è un fallimento: i soldi sono
      // partiti e verranno accreditati. Va detto in questi termini.
      setDepositMsg(err.code === 'ANCORA_IN_CORSO' ? err.message : `Deposit failed: ${err.message}`);
    } finally {
      setIsDepositing(false);
    }
  };

  // Limiti dei prelievi: una volta all'avvio, come la configurazione di catena.
  useEffect(() => {
    let annullato = false;
    fetchWithdrawConfig()
      .then((c) => {
        if (annullato) return;
        setWithdrawConfig(c);
        setWithdrawAmount(String(c.minSol));
      })
      .catch(() => { if (!annullato) setWithdrawConfig(null); });
    return () => { annullato = true; };
  }, []);

  // Prelievo: l'utente firma un'AUTORIZZAZIONE leggibile (off-chain, gratis) e
  // la transazione la manda il server dalla tesoreria. La destinazione non si
  // sceglie da qui: si viene pagati sul wallet con cui si è connessi, punto.
  const handleWithdraw = async () => {
    if (isWithdrawing) return;
    if (!authed) {
      alert('Sign the wallet login to withdraw.');
      return;
    }

    const sol = Number(withdrawAmount);
    if (!Number.isFinite(sol) || sol <= 0) {
      setWithdrawMsg('Invalid amount.');
      return;
    }

    setIsWithdrawing(true);
    setWithdrawMsg('');
    try {
      const esito = await preleva({
        wallet: walletAddress,
        signMessage,
        sol,
        onProgress: setWithdrawMsg,
      });

      setSolBalance(esito.newBalance);
      setWithdrawMsg(
        esito.review
          ? `Request for ${esito.amount} SOL received: above the threshold it needs a manual review.`
          : `Withdrawal of ${esito.amount} SOL sent. Signature: ${esito.signature || '—'}`
      );
    } catch (err) {
      setWithdrawMsg(`Withdrawal failed: ${err.message}`);
    } finally {
      setIsWithdrawing(false);
    }
  };

  // Scorciatoia di sviluppo: entra in partita senza Phantom. Non salta piu'
  // l'ingresso vero — fa login come pseudo-wallet "dev-..." e chiede un ticket
  // come tutti gli altri. Saltare il percorso avrebbe voluto dire sviluppare
  // ogni giorno su una strada diversa da quella che va in produzione.
  // Il server rifiuta il dev-login quando NODE_ENV=production.
  const handleDevEnter = async () => {
    if (isDeploying) return;
    setIsDeploying(true);
    try {
      await devLogin(DEV_WALLET);
      // La scorciatoia entrava sempre nel tier 1, e per provare le altre due
      // arene serviva un wallet finanziato e il giro completo della lobby —
      // motivo per cui il colore per arena non si era mai potuto guardare da
      // qui. Con `?tier=2` nell'indirizzo si entra dove si vuole. Vale solo
      // fuori produzione, come tutto questo pulsante.
      const tierChiesto = Number(new URLSearchParams(window.location.search).get('tier')) || 1;
      const ticket = await requestEntryTicket(DEV_WALLET, tierChiesto);
      onEnterGame(DEV_WALLET, ticket.tier, equippedSkin, ticket.ticketId);
    } catch (err) {
      alert(`Dev entry failed: ${err.message}`);
    } finally {
      setIsDeploying(false);
    }
  };

  // Acquisto ed equipaggiamento passano dal server: prezzo, saldo e possesso li
  // decide lui. Il client si limita a mostrare il profilo che riceve indietro.
  const applyProfile = (profile) => {
    setUnlockedSkins(profile.unlockedSkins || []);
    setEquippedSkin(profile.equippedSkin);
    setLocalVexlBalance(profile.vexlBalance || 0);
    // Il saldo interno arriva dal profilo, non solo dall'ultimo deposito fatto
    // in questa sessione: senza, dopo un ricaricamento della pagina mostrava 0
    // anche con dei SOL depositati, e sembrava che il deposito fosse sparito.
    setSolBalance(profile.solBalance || 0);
  };

  const handleBuySkin = async (skin) => {
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const owned = unlockedSkins.includes(skin.hex);

    // Senza wallet connesso non c'e' profilo a cui legare la skin.
    if (!walletAddress) {
      if (owned) setEquippedSkin(skin.hex);
      return;
    }

    // Le azioni sul vault richiedono il login a firma: il server rifiuta senza token.
    if (!authed) {
      alert('Sign the wallet login to manage skins.');
      return;
    }

    // Il wallet non va più nel body: lo ricava il server dal token. Così nessuno
    // può agire per conto di un altro wallet.
    const authedPost = (endpoint, skinId) => fetch(`${API_BASE}/api/skins/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader(walletAddress) },
      body: JSON.stringify({ skinId })
    });

    try {
      const res = await authedPost(owned ? 'equip' : 'buy', skin.hex);
      const data = await res.json();

      if (!res.ok) {
        alert(data.error || 'Operation failed');
        return;
      }

      applyProfile(data);

      // Appena comprata: la equipaggiamo subito, come faceva prima il client. Il
      // burn del $VEXL speso lo fa ora il server dentro l'acquisto; qui aggiorniamo
      // solo il contatore mostrato, che l'acquisto restituisce.
      if (!owned) {
        const equipRes = await authedPost('equip', skin.hex);
        if (equipRes.ok) applyProfile(await equipRes.json());
        if (typeof data.totalBurnedVexl === 'number') setGlobalBurn(data.totalBurnedVexl);
      }
    } catch (err) {
      console.error('[VEXEL] Operazione skin fallita:', err);
      alert('Server unreachable: try again.');
    }
  };


  // Pagina aperta dentro Phantom per firmare al posto del browser normale: qui
  // non c'e' niente da giocare, e mostrare la lobby inviterebbe a restare nel
  // posto sbagliato — quella finestra non ruota. Si dice solo com'e' andata e
  // si manda indietro.
  if (segretoConsegna) {
    return (
      <div className="landing-container">
        <div className="landing-content glass-panel consegna-schermo">
          <h1 className="glitch-title">{consegnato ? 'SESSION READY' : 'AUTHORIZING…'}</h1>
          <p className="subtitle">
            {consegnato
              ? 'Taking you back to your browser: the match is waiting there.'
              : 'Approve the signature in Phantom. Do not close this page.'}
          </p>
          {consegnato && (
            <>
              {/* Il pulsante resta anche quando il rientro automatico riesce, e
                  non è una ridondanza: nessuna pagina può cambiare app da sola,
                  può solo chiedere: è la webview di Phantom a decidere se
                  lasciar passare la richiesta. Un tocco vero ha molte più
                  probabilità di essere accettato di una navigazione automatica,
                  e se anche quella strada fosse chiusa qui c'è comunque scritto
                  cosa fare. */}
              {ritorno && (
                <button
                  className="btn-neon-glitch"
                  onClick={() => { tornaAlBrowser(ritorno); }}
                >
                  BACK TO YOUR BROWSER
                </button>
              )}
              <p className="auth-panel-text">
                {ritorno
                  ? 'Still here? Switch back by hand: your session is already open there.'
                  : 'Go back to your browser: the game unlocks by itself as soon as you return.'}
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="landing-container">
      {/* Stesse impostazioni audio della partita: stesso componente, stesso storage. */}
      <SettingsMenu />

      {/* Accanto alla rotella, e solo a wallet collegato. Prima non esisteva
          alcun modo di scollegarsi: `WalletMultiButton` — che sul desktop
          conterrebbe la voce «Disconnect» — sparisce proprio quando ci si
          collega, e da telefono non viene montato affatto. */}
      {connected && (
        <button
          className="vx-disconnect"
          onClick={handleDisconnect}
          title={walletAddress ? `Disconnect ${walletAddress.slice(0, 4)}…${walletAddress.slice(-4)}` : 'Disconnect wallet'}
        >
          DISCONNECT
        </button>
      )}

      {/* Fascia superiore: livello a sinistra, contatore del bruciato al centro. */}
      <nav className="top-navbar glass-panel">
        {/* Il livello vive dentro la fascia, a sinistra. Compare a wallet connesso:
            è del wallet, non del browser. */}
        {levelInfo && (
          <div className="vx-level-badge" title={levelInfo.maxed
            ? 'Max level'
            : `${levelInfo.xpIntoLevel.toLocaleString()} / ${levelInfo.xpForNextLevel.toLocaleString()} XP`}>
            <span className="vx-level-num">LVL {levelInfo.level}</span>
            <span className="vx-level-bar">
              <span className="vx-level-fill" style={{ width: `${levelInfo.percent}%` }} />
            </span>
            <span className="vx-level-xp">
              {levelInfo.maxed ? 'MAX' : `${levelInfo.xpToNext.toLocaleString()} XP`}
            </span>
          </div>
        )}

        <div className="global-burn-counter glitch-text" data-text={`TOTAL $VEXL BURNED: ${globalBurn.toLocaleString()}`}>
          🔥 TOTAL $VEXL BURNED: {globalBurn.toLocaleString()}
        </div>
      </nav>

      <div className="landing-content glass-panel">
        <h1 className="glitch-title glitch-text" data-text="THE STAGING AREA">THE STAGING AREA</h1>
        <p className="subtitle">Connect your wallet to enter the arena.</p>

        {!connected ? (
          <div className="connect-section">
            {phantomBrowserNeeded ? (
              <>
                <button
                  className="btn-connect"
                  disabled={phantom.inCorso}
                  onClick={phantom.collega}
                >
                  {phantom.inCorso ? 'WAITING FOR PHANTOM…' : 'CONNECT PHANTOM'}
                </button>
                {phantom.errore && (
                  <p className="deeplink-error">{phantom.errore}</p>
                )}
                {/* Il vault (comprare e cambiare skin) richiede la firma, che
                    da qui non si puo' ottenere: Phantom la rifiuta. Chi ne ha
                    bisogno passa dal suo browser interno, dove pero' non si
                    gioca perche' quella finestra non ruota. */}
                <button className="btn-vault-link" onClick={apriInPhantom}>
                  vault access → open in Phantom
                </button>
              </>
            ) : (
              <WalletMultiButton className="btn-connect" />
            )}
            <div style={{ marginTop: '30px', display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'center' }}>
              <button onClick={handleDevEnter} className="btn-dev" disabled={isDeploying}>
                {isDeploying ? 'REQUESTING CLEARANCE…' : 'DEV: ENTER GAME (SKIP)'}
              </button>
            </div>
          </div>
        ) : (
          <div className="lobby-section">
            {/* Manca la firma: il wallet e' collegato ma il server non sa
                ancora che e' suo, e senza token /api/entry rifiuta il biglietto
                d'ingresso. Da telefono e' una tappa a se', con un salto fuori
                dal browser, e DEVE partire da un tocco. */}
            {phantom.daFirmare && (
              <div className="auth-panel">
                <p className="auth-panel-text">
                  Wallet connected. One more signature to enter the arena.
                </p>
                <button
                  className="btn-connect"
                  onClick={phantom.firma}
                  disabled={phantom.inCorso}
                >
                  {phantom.inCorso ? 'WAITING FOR PHANTOM…' : 'SIGN IN WITH PHANTOM'}
                </button>
                {phantom.errore && (
                  <p className="deeplink-error">{phantom.errore}</p>
                )}
              </div>
            )}

            {/* Wallet Info (Always visible) */}
            <div className="wallet-info">
              <div className="wallet-address">OPERATIVE: {walletAddress?.slice(0,4)}...{walletAddress?.slice(-4)}</div>
              <div className="wallet-balances">
                <span>💰 {solBalance} SOL</span>
                <span>☢️ {localVexlBalance.toLocaleString()} $VEXL</span>
              </div>
              {/* Deposito vero: firma una transazione verso la tesoreria e il
                  server accredita l'importo che legge in catena. Il pulsante
                  "TEST DEPOSIT 1 SOL" che stava qui accreditava un SOL dal
                  nulla, perché il server si fidava del numero nel body. */}
              {chainConfig?.depositsEnabled && (
                <div style={{ marginTop: '10px' }}>
                  <button
                    onClick={handleDeposit}
                    disabled={isDepositing || !authed}
                    title={!authed ? 'Firma il login col wallet per depositare' : ''}
                    style={{
                      background: 'var(--toxic-green)', color: '#000', padding: '5px 15px',
                      borderRadius: '5px', border: 'none', fontWeight: 'bold',
                      cursor: isDepositing || !authed ? 'not-allowed' : 'pointer',
                      opacity: isDepositing || !authed ? 0.5 : 1,
                    }}
                  >
                    {isDepositing ? 'DEPOSITO IN CORSO…' : `+ DEPOSITA ${DEPOSITO_SOL} SOL`}
                  </button>
                  {depositMsg && (
                    <div style={{ marginTop: '6px', fontSize: '0.75rem', color: '#aaa' }}>{depositMsg}</div>
                  )}
                  {/* Phantom simula la transazione sulla rete selezionata AL SUO
                      INTERNO, che è indipendente da quella dell'app. Con Phantom
                      su Mainnet e l'app su devnet, il blockhash devnet non esiste
                      per Phantom e compare "Simulazione della transazione non
                      riuscita" — un avviso che spaventa e che non dipende dalla
                      transazione, che è valida e infatti va a buon fine. */}
                  {chainConfig.cluster !== 'mainnet-beta' && (
                    <div style={{ marginTop: '6px', fontSize: '0.7rem', color: '#666', maxWidth: '320px' }}>
                      Rete <b>{chainConfig.cluster}</b>: imposta la stessa rete anche in Phantom
                      (Impostazioni → Sviluppatore → Testnet Mode), altrimenti mostrerà
                      «Simulazione della transazione non riuscita» pur essendo tutto a posto.
                    </div>
                  )}
                </div>
              )}

              {/* Prelievo: attivo solo se questo server ha la chiave della
                  tesoreria. La destinazione non è un campo — si viene pagati
                  sul wallet della sessione, ed è il motivo per cui qui c'è solo
                  un importo. */}
              {withdrawConfig?.enabled && (
                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="number"
                      min={withdrawConfig.minSol}
                      step="0.01"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      disabled={isWithdrawing || !authed}
                      style={{
                        width: '90px', padding: '5px', borderRadius: '5px',
                        border: '1px solid #444', background: '#111', color: '#fff',
                      }}
                    />
                    <button
                      onClick={handleWithdraw}
                      disabled={isWithdrawing || !authed}
                      title={!authed ? 'Firma il login col wallet per prelevare' : ''}
                      style={{
                        background: 'transparent', color: 'var(--toxic-green)',
                        padding: '5px 15px', borderRadius: '5px',
                        border: '1px solid var(--toxic-green)', fontWeight: 'bold',
                        cursor: isWithdrawing || !authed ? 'not-allowed' : 'pointer',
                        opacity: isWithdrawing || !authed ? 0.5 : 1,
                      }}
                    >
                      {isWithdrawing ? 'PRELIEVO IN CORSO…' : '↑ PRELEVA SOL'}
                    </button>
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#666' }}>
                    min {withdrawConfig.minSol} SOL · max {withdrawConfig.dailyMaxSol} SOL/giorno · verso il tuo wallet
                  </div>
                  {withdrawMsg && (
                    <div style={{ fontSize: '0.75rem', color: '#aaa', wordBreak: 'break-all', maxWidth: '320px' }}>
                      {withdrawMsg}
                    </div>
                  )}
                </div>
              )}

              {/* Prelievi spenti: si dice perché. Nascondere e basta faceva
                  pensare a un problema di saldo, che non c'entra nulla — il
                  server semplicemente non ha la chiave per firmare le uscite. */}
              {withdrawConfig && !withdrawConfig.enabled && (
                <div style={{ marginTop: '8px', fontSize: '0.7rem', color: '#666' }}>
                  Withdrawals are off on this server (the treasury key is missing).
                </div>
              )}
            </div>

            {/* Dashboard Tabs */}
            <div className="dashboard-tabs">
              <button
                className={`tab-btn ${activeTab === 'ARENA' ? 'active' : ''}`}
                onClick={() => setActiveTab('ARENA')}
              >
                ARENA
              </button>
              <button
                className={`tab-btn ${activeTab === 'VAULT' ? 'active' : ''}`}
                onClick={() => setActiveTab('VAULT')}
              >
                SKIN VAULT
              </button>
              <button
                className={`tab-btn ${activeTab === 'MISSIONS' ? 'active' : ''}`}
                onClick={() => setActiveTab('MISSIONS')}
              >
                MISSIONS
              </button>
              <button
                className={`tab-btn ${activeTab === 'HOWTO' ? 'active' : ''}`}
                onClick={() => setActiveTab('HOWTO')}
              >
                HOW TO PLAY
              </button>
            </div>

            {/* Tab Contents */}
            <div className="tab-content">
              {activeTab === 'ARENA' && (
                <div className="arena-tab">
                  <h3 className="select-tier-title">SELECT COMBAT TIER</h3>
                  {/* I prezzi arrivano dal server: erano scritti qui a mano
                      ("0.005 SOL (~$1)"), e quel "$1" era vero solo con il SOL a
                      200 dollari. Ora la cifra in SOL e' quella davvero
                      addebitata e i dollari sono una stima al cambio corrente. */}
                  <div className="tier-cards">
                    {(pricingInfo?.tiers || []).map((t) => (
                      <div
                        key={t.tier}
                        className={`tier-card ${selectedTier === t.tier ? 'selected' : ''} tier-${t.tier}`}
                        onClick={() => setSelectedTier(t.tier)}
                      >
                        <h4>{TIER_EMOJI[t.tier]} {t.label}</h4>
                        <p>
                          {t.price > 0
                            ? `Ingresso: ${conStimaInDollari(t.price, solUsd)}`
                            : 'Ingresso: GRATUITO (fase di test)'}
                        </p>
                        <small>Posta in gioco: {conStimaInDollari(t.stake, solUsd)}</small>
                      </div>
                    ))}
                    {!pricingInfo && <p style={{ color: '#888' }}>Caricamento arene…</p>}
                  </div>

                  {/* Il requisito si mostra solo se esiste davvero: finché il
                      token non è in circolazione il server dichiara 0 e qui non
                      compare niente, invece di annunciare una regola che non
                      viene applicata. */}
                  {pricingInfo?.vexlGate?.enabled && (
                    <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                      <small style={{ color: '#fff' }}>
                        ACCESSO {TIER_EMOJI[selectedTier]}: richiesti{' '}
                        <strong>
                          {(pricingInfo.tiers.find((t) => t.tier === selectedTier)?.vexlMin || 0).toLocaleString()} $VEXL
                        </strong>{' '}
                        nel wallet (non si spendono)
                      </small>
                    </div>
                  )}

                  <button
                    onClick={handleDeploy}
                    className="btn-deploy"
                    disabled={isDeploying}
                  >
                    {isDeploying ? 'REQUESTING CLEARANCE…' : 'DEPLOY TO ARENA'}
                  </button>
                </div>
              )}

              {activeTab === 'VAULT' && (
                <div className="vault-tab">
                  <h3>SKIN VAULT</h3>
                  <p>Spend $VEXL to unlock exclusive snake skins. All spent $VEXL is burned.</p>

                  {/* Meglio dirlo che far credere al giocatore di aver comprato
                      qualcosa che il server non ha mai registrato. */}
                  {!profileLoaded && (
                    <p style={{ color: '#ff0055', fontSize: '11px' }}>
                      ⚠️ PROFILE OFFLINE — showing local cache, purchases are disabled.
                    </p>
                  )}
                  {profileLoaded && !authed && (
                    <p style={{ color: '#ffb300', fontSize: '11px' }}>
                      🔒 SIGN THE LOGIN IN YOUR WALLET TO BUY OR EQUIP SKINS.
                    </p>
                  )}

                  <div className="skins-grid">
                    {availableSkins.map(skin => {
                      const isUnlocked = unlockedSkins.includes(skin.hex);
                      const isEquipped = equippedSkin === skin.hex;
                      const canAfford = localVexlBalance >= skin.price;
                      const isLockedRequirement = skin.lockedMessage && !isUnlocked;
                      const borderColor = skin.type === 'color' ? skin.hex : '#39FF14'; // Default border for images

                      return (
                        <div key={skin.id} className={`skin-card ${isEquipped ? 'equipped' : ''}`} style={{ borderColor: borderColor }}>
                          <div className="skin-preview" style={{ backgroundColor: skin.type === 'color' ? skin.hex : 'transparent' }}>
                            {skin.type === 'image' && (
                              <img src={`/assets/${skin.preview || skin.hex}.png?v=${VERSIONE_ASSET}`} alt={skin.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            )}
                          </div>
                          <div className="skin-info">
                            <h4>{skin.name}</h4>
                            <p>{skin.price === 0 ? 'FREE' : `${skin.price.toLocaleString()} $VEXL`}</p>
                            {isLockedRequirement && <p style={{ color: '#ff0055', fontSize: '10px' }}>{skin.lockedMessage}</p>}
                          </div>
                          <button
                            className={`btn-skin ${isEquipped ? 'btn-equipped' : ''} ${(!isUnlocked && !canAfford) || isLockedRequirement ? 'disabled' : ''}`}
                            disabled={((!isUnlocked && !canAfford) || isLockedRequirement) || isEquipped}
                            onClick={() => handleBuySkin(skin)}
                            style={{ color: isEquipped ? '#000' : borderColor, backgroundColor: isEquipped ? borderColor : 'transparent', borderColor: borderColor }}
                          >
                            {isEquipped ? 'EQUIPPED' : isUnlocked ? 'EQUIP' : isLockedRequirement ? 'LOCKED' : 'BUY & BURN'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {activeTab === 'MISSIONS' && (
                <div className="missions-tab">
                  <h3>MISSIONS</h3>
                  <p>Complete objectives in-game to unlock skins. Progress is tracked by the server.</p>

                  {!walletAddress && (
                    <p style={{ color: '#ffb300', fontSize: '11px' }}>
                      Connect your wallet to see your mission progress.
                    </p>
                  )}

                  <div className="missions-list">
                    {missions.map(mission => {
                      const percent = Math.min(100, Math.round((mission.progress / mission.target) * 100));
                      const rewardSkin = mission.reward && mission.reward.skin;

                      return (
                        <div key={mission.id} className={`mission-card ${mission.completed ? 'complete' : ''}`}>
                          <div className="mission-details">
                            <h4>{mission.title}</h4>
                            <div className="progress-bar-container">
                              <div className="progress-bar-fill" style={{ width: `${percent}%` }}></div>
                            </div>
                            <span className="progress-text">
                              {percent}%{rewardSkin ? ` · unlocks ${rewardSkin}` : ''}
                            </span>
                          </div>

                          <button className="btn-claim" disabled>
                            {mission.completed ? 'DONE' : 'IN PROGRESS'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Qui c'era il white paper. Spiegava la tokenomics a chi sta
                  valutando il progetto, ma chi e' fermo in staging area col
                  wallet gia' collegato ha superato quella fase: gli serve
                  sapere come si gioca e cosa sono le cose che vede a terra. */}
              {activeTab === 'HOWTO' && (
                <div className="howto-tab" style={{ textAlign: 'left', animation: 'fadeIn 0.4s ease-out' }}>
                  <HowToPlay />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LandingPage;
